import React from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { updateBreadcrumb } from '#action/breadcrumb';
import { clearMeasurements } from '#action/device/measurements';
import { MapWithPreloader as Map } from '#component/map';
import MultipleSelectDevice from '#component/form/multiple-select';
import { mapToSelect, mapSelected, formatter } from '#component/shipment/helper';
import { locationsSelector } from '#selector/shipment/overview';
import { CLEAR_SHIPMENT, WARNING } from '#action/types';
import DefaultPreloader from '#component/preloader';
import { getShipmentDateRange, getChartRange, mergeEvents, isIdentifiersLoaded, getEventsWithAcceptance, getDevicesWithAxis } from './helper';
import propTypes from './prop-types';
import DesktopView from './components/desktop-view';
import Header from './components/header';
import MobileView from './components/mobile-view';
import { OverviewCtn } from './style';
import { getProjectDateRange, setDocumentTitle, getMetricsIdentifiers, getOverviewHeaderHeight, detectExcursions } from '#helper';

const DeviceMeasurements = React.lazy(() => import('./measurements'));

const FOUR_HOURS = 1000 * 60 * 60 * 4;
const MAP_TAB = 90;

const isWidthDown = (theme, breakpoint, width) =>
  theme.breakpoints.values[width] < theme.breakpoints.values[breakpoint];

export default class ShipmentOverview extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      rangeStart: null,
      rangeEnd: null,
      rangeInterval: null,
      shipmentLoading: true,
      clickedDevices: [],
      clickedEvents: [],
      identifiersLoading: true,
      identifiers: [],
      tabSelected: 0
    };
  }

  componentDidMount() {
    this.fetchData();

    while (document.getElementsByClassName('d3-tip').length) {
      document.body.removeChild(document.getElementsByClassName('d3-tip')[0]);
    }
  }

  componentDidUpdate(prevProps) {
    const { shipment: { uid }, dispatch } = this.props;
    if (uid && uid !== prevProps.shipment.uid) {
      dispatch(
        updateBreadcrumb([
          { label: 'Shipments', path: '/shipment' },
          { label: formatter(uid) }
        ])
      );
    }
  }

  componentWillUnmount() {
    this.props.dispatch(clearMeasurements());
    window.removeEventListener('scroll', this.scroll);
    this.props.dispatch({ type: CLEAR_SHIPMENT });
  }

  fetchData = async () => {
    const {
      match: { params: { shipmentId } },
      fetchShipmentAndDelivery,
      fetchLocations,
      fetchMeta,
      fetchMetricsByIdentifier,
      fetchShipmentEvents,
      fetchShipmentFiles,
      fetchCargoFiles
    } = this.props;

    await Promise.all([
      fetchShipmentAndDelivery(shipmentId),
      fetchShipmentFiles(shipmentId),
      fetchMeta()
    ]);

    const {
      shipment,
      shipment: { defaultDevices, label, uid },
      match: { params: { tabNum } },
      meta
    } = this.props;

    setDocumentTitle('Shipment dashboard', label || formatter(uid));

    const { rangeStart, rangeEnd, rangeInterval } = getShipmentDateRange(shipment);
    const identifiers = getMetricsIdentifiers(defaultDevices, meta);
    const loadings = identifiers.reduce((a, c) => ({ ...a, [`${c}Loading`]: true }), {});

    await this.setState({
      rangeStart,
      rangeEnd,
      rangeInterval,
      selectedDevices: defaultDevices,
      shipmentLoading: false,
      identifiers,
      ...loadings,
      identifiersLoading: false,
      locationsLoading: true
    });
    await this.setState({ headerHeight: getOverviewHeaderHeight() });

    const tab = tabNum ? parseInt(tabNum, 10) : 90;

    let firstTabRequest;
    const restTabsRequests = [
      ...identifiers.map(identifier =>
        ({ fn: fetchMetricsByIdentifier, args: [shipmentId, null, identifier], identifier })),
      { fn: fetchShipmentEvents, args: [shipmentId] },
      { fn: fetchLocations, args: [shipmentId], identifier: 'locations' },
      { fn: fetchCargoFiles, args: [shipmentId] }
    ];

    if (identifiers[tab]) {
      [firstTabRequest] = restTabsRequests.splice(tab, 1);
    } else if (tab === MAP_TAB) {
      firstTabRequest = restTabsRequests.pop();
    } else {
      firstTabRequest = restTabsRequests.shift();
    }

    const requestCallback = identifier => () => {
      const { shipment: { defaultDevices: dd1 } } = this.props;
      const { selectedDevices } = this.state;
      const state = {
        selectedDevices: dd1.filter(dd => selectedDevices.find(sd => sd.id === dd.id))
      };
      if (identifier) {
        state[`${identifier}Loading`] = false;
      }
      this.setState(state);
    };

    await firstTabRequest.fn.call(this, ...firstTabRequest.args)
      .then(requestCallback(firstTabRequest.identifier));

    await Promise.all([
      ...restTabsRequests.map(rtr => rtr.fn.call(this, ...rtr.args)
        .then(requestCallback(rtr.identifier)))
    ]);
  };

  setChart = chart => {
    this.chart = chart;
  };

  setMap = map => {
    this.map = map;
  };

  getDateData = () => {
    const { account, shipment: { states: delivery = [] } } = this.props;
    const minMaxDate = {};
    let shipmentDateRange = '';
    const beginDate = (delivery[0] && delivery[0].date) || new Date();
    const endDate = (delivery.length > 1 && delivery[delivery.length - 1].date) || null;

    minMaxDate.minDate = new Date(beginDate);
    const opt = {};
    opt.beginDate = beginDate;

    if (endDate) {
      minMaxDate.maxDate = new Date(endDate);
      opt.expirationDate = endDate;
    }

    shipmentDateRange = getProjectDateRange(opt, account && account.measurementSystem);

    return {
      minMaxDate,
      shipmentDateRange
    };
  };

  tabProps = (value, isExcursion) => {
    const { match: { params: { shipmentId } } } = this.props;
    return {
      value,
      tabIndex: 0,
      className: cn('overview-tab', { colorRed: isExcursion }),
      component: Link,
      to: `/shipment/${shipmentId}/t/${value}`,
      replace: true,
      onClick: () => {
        this.setState({
          clickedDevices: [],
          clickedEvents: [],
          clickedAxis: []
        });
      }
    };
  };

  handleTimelineHover = (event, checkpoint) => {
    const { match: { params: { tabNum } } } = this.props;
    const tab = tabNum ? parseInt(tabNum, 10) : 90;
    if (tab !== MAP_TAB) {
      const date = checkpoint.date || checkpoint.done;
      if (this.chart && date) {
        this.chart.highlightTimestamp = event.type === 'mouseenter' ? new Date(date) : null;
        this.chart.highlightEvent(this.chart.highlightTimestamp);
      }
    } else if (this.map && checkpoint.locations) {
      this.map.app.highlightCheckpoint(checkpoint, event.type === 'mouseenter');
    }
  };

  handleTimelineClick = checkpoint => {
    const { dispatch, match: { params: { tabNum } } } = this.props;
    const tab = tabNum ? parseInt(tabNum, 10) : 90;
    if (tab !== MAP_TAB && this.chart) {
      const date = checkpoint.date || checkpoint.done;
      if (!date) {
        dispatch({
          type: WARNING,
          payload: {
            message: "Checkpoint doesn't have date"
          }
        });
      } else {
        this.chart.moveChart(new Date(date), true);
      }
    } else if (this.map) {
      if (!checkpoint.locations) {
        dispatch({
          type: WARNING,
          payload: {
            message: "Checkpoint doesn't have location"
          }
        });
      } else {
        const { locations: [{ lat, lng }] } = checkpoint;
        this.map.app.clickCheckpoint(lat, lng);
      }
    }
  };

  handleMultipleDevicesClick = devices => {
    // "Escape"
    const v = Array.isArray(devices) ? devices : [];
    this.setState({ clickedDevices: v });
  };

  handleMultipleEventsClick = events => {
    // "Escape"
    // const e = Array.isArray(events) ? events : [];
    this.setState({ clickedEvents: events });
  };

  handleEventHover = (event, item) => {
    if (this.chart) {
      this.chart.highlightTimestamp = event.type === 'mouseenter' ? new Date(item.timestamp) : null;
      this.chart.highlightEvent(this.chart.highlightTimestamp);
    }
  };

  handleEventGroupHover = (event, item) => {
    if (this.chart) {
      this.chart.highlightTimestamp = event.type === 'mouseenter' ? getChartRange(item) : null;
      this.chart.highlightEventGroup(this.chart.highlightTimestamp);
    }
  };

  handleEventClick = (event, item) => {
    const { dispatch, match: { params: { tabNum } } } = this.props;
    const tab = tabNum ? parseInt(tabNum, 10) : 90;
    if (tab !== MAP_TAB) {
      if (this.chart) {
        if (Array.isArray(item)) {
          this.chart.highlightTimestamp = getChartRange(item);
          this.chart.highlightEventGroup(this.chart.highlightTimestamp);
        }
        this.chart.moveChart(getChartRange(item));
      }
    } else if (!item.latitude || !item.longitude) {
      dispatch({
        type: WARNING,
        payload: {
          message: "Event doesn't have coordinates"
        }
      });
    } else if (this.map) {
      this.map.app.clickCheckpoint(item.latitude, item.longitude);
    }
  };

  handleEventGroupClosing = (event, item) => {
    const { match: { params: { tabNum } } } = this.props;
    const tab = tabNum ? parseInt(tabNum, 10) : 90;

    if (tab !== MAP_TAB && this.chart) {
      this.chart.resetScale();
      this.chart.highlightTimestamp = getChartRange(item);
      this.chart.highlightEventGroup(this.chart.highlightTimestamp);
    }
  };

  getSelectedTabName = () => {
    const { match: { params: { tabNum } } } = this.props;
    const { identifiers } = this.state;

    return identifiers[tabNum];
  };

  handleMeasurementClick = clickedDevices => {
    const { defaultDevices } = this.props.shipment;
    const { selectedDevices } = this.state;
    const selectedUids = new Set(selectedDevices.map(({ uid }) => uid));
    const clickedUids = new Set(clickedDevices?.map(({ uid }) => uid));

    // mutates the data to show the state when metrics requests are in progress
    // TODO: use a state
    defaultDevices.forEach(device => {
      if (selectedUids.has(device.uid)) {
        device.tinted = !clickedUids.has(device.uid);
      }
    });

    this.setState({
      clickedAxis: clickedDevices,
      tabSelected: 1
    });
  };

  syncSelectedDevicesWithStore = (currentEvent, acceptance) => {
    const { shipment: { defaultDevices } } = this.props;
    const { clickedEvents } = this.state;

    // set clicked events
    if (clickedEvents.length) {
      const newClickedEvents = getEventsWithAcceptance(clickedEvents, currentEvent, acceptance);
      this.handleMultipleEventsClick(newClickedEvents);
    }

    this.setState({ selectedDevices: defaultDevices });
  };

  render() {
    const {
      shipment,
      location,
      shipment: {
        defaultDevices,
        eventStatus,
        states,
        metricsLoadingStatus,
        events: shipmentEvents,
        shipmentStatus,
        project
      },
      copiedShipment,
      account,
      match: { params: { tabNum } },
      width,
      theme,
      history
    } = this.props;
    const {
      rangeStart,
      rangeEnd,
      rangeInterval,
      selectedDevices,
      shipmentLoading,
      identifiers,
      identifiersLoading,
      clickedDevices,
      clickedEvents,
      headerHeight
    } = this.state;

    if (shipmentLoading || copiedShipment.status === 'loading') {
      return <DefaultPreloader />;
    }

    const { shipmentDateRange } = this.getDateData();
    const locations = locationsSelector(defaultDevices);
    const tab = tabNum ? parseInt(tabNum, 10) : 90;
    const isWidthDownMd = isWidthDown(theme, 'md', width);
    const isWidthDownSm = isWidthDown(theme, 'sm', width);
    const isLastLocation = new URLSearchParams(location.search).has('lastlocation');

    const isEventsExist = selectedDevices.some(sd => Boolean(sd?.events));
    const events = mergeEvents([
      ...shipmentEvents,
      ...isEventsExist ? selectedDevices.flatMap(sd => sd?.events) : []
    ]);

    const eventsLoadingStatus = eventStatus !== 'succeeded' || eventStatus === 'idle';
    const isMetricsLoaded = isIdentifiersLoaded(identifiers, this.state);

    const stopped = [3, 4].includes(shipmentStatus.code);

    const timeoutAlerts = !stopped ? defaultDevices.filter(d => !d.lastMetric?.timestamp ||
        new Date().getTime() - new Date(d.lastMetric?.timestamp).getTime() > FOUR_HOURS) : [];

    const devicesWithExcursions = !stopped ? defaultDevices.filter(d =>
      // a device group has an alert
      d.alerts?.length &&
      // an alert for a device is switched ON
      d.projectAlertId &&
      shipment?.alerts.find(a => {
        d.excursions = a.id === d.projectAlertId ?
          detectExcursions(a, d.metrics) :
          [];
        return d.excursions.length;
      })) : [];

    const measurementTab = this.getSelectedTabName();
    const defaultDevicesWithAxisInfo = getDevicesWithAxis(
      defaultDevices,
      this.state.clickedAxis,
      measurementTab
    );
    const selectedDevicesWithAxisInfo = getDevicesWithAxis(
      selectedDevices,
      this.state.clickedAxis,
      measurementTab
    );

    const MapComponent = <Map
      className="overview-map"
      devices={defaultDevices.filter(dd => selectedDevices.find(sd => sd.id === dd.id))}
      locations={locations}
      events={events}
      setMap={this.setMap}
      destinations={states}
      clickDevices={this.handleMultipleDevicesClick}
      loading={this.state.locationsLoading}
      isMobile={isWidthDownSm}
      zoomLastLocation={isLastLocation}
      isMetricsLoaded={isMetricsLoaded}
    />;

    const sidebarComponentsProps = {
      shipment,
      events,
      account,
      eventsLoading:
        eventsLoadingStatus || (selectedDevices.length > 0 && !isEventsExist && !events.length),
      isMobile: isWidthDownMd,
      identifiersMap: {
        0: 'timeline',
        1: 'devices',
        2: 'events',
        3: 'info'
      },
      tabSelected: this.state.tabSelected,
      setTabSelected: t => this.setState({ tabSelected: t }),
      handleTimelineHover: this.handleTimelineHover,
      handleTimelineClick: this.handleTimelineClick,
      handleEventGroupHover: this.handleEventGroupHover,
      handleEventGroupClosing: this.handleEventGroupClosing
    };

    const sidebarProps = {
      handleEventClick: this.handleEventClick,
      handleEventHover: this.handleEventHover
    };

    const sidebarSharedProps = {
      clickedDevices,
      clickedEvents,
      setClickedDevices: this.handleMultipleDevicesClick,
      setClickedEvents: this.handleMultipleEventsClick,
      syncSelectedDevicesWithStore: this.syncSelectedDevicesWithStore
    };

    const deviceOverviewProps = {
      parentState: this.state,
      DeviceMeasurements,
      identifiers,
      tab,
      setChart: this.setChart,
      MapComponent,
      events,
      rangeStart,
      rangeEnd,
      rangeInterval,
      selectedDevices: selectedDevicesWithAxisInfo,
      tabProps: this.tabProps,
      account,
      history
    };

    const sidebarDevicesProps = {
      devices: mapToSelect(defaultDevicesWithAxisInfo, account.measurementSystem),
      selectedDevices: mapToSelect(selectedDevicesWithAxisInfo, account.measurementSystem),
      setDevices: async (devices, restoreTinted) => {
        if (restoreTinted) {
          defaultDevicesWithAxisInfo.forEach(d => {
            d.tinted = false;
            d.selectedAxis = undefined;
          });
        } else {
          const deviceUids = devices.map(d => d.uid);
          defaultDevicesWithAxisInfo
            .filter(d => !deviceUids.includes(d.uid))
            .forEach(d => {
              d.tinted = false;
              d.selectedAxis = undefined;
            });
        }

        const sd = mapSelected(defaultDevicesWithAxisInfo, devices);
        await this.setState({ selectedDevices: sd, clickedAxis: [] });
        if (this.map) {
          this.map.app.updateSelectedDevices(sd);
        }
      },
      rangeStart,
      rangeEnd,
      timeoutAlerts,
      devicesWithExcursions,
      account
    };

    return (
      <OverviewCtn className="cluster overview">
        <Header
          shipmentDateRange={shipmentDateRange}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          project={project}
          eventStatus={eventStatus}
          isMetricsLoaded={isMetricsLoaded}
          shipment={shipment}
          account={account}
        >
          {!isWidthDownSm ? null :
          <MultipleSelectDevice
            id="shipment-device-filter-multiple-select"
            label="Devices"
            placeholder="Devices"
            data={mapToSelect(defaultDevicesWithAxisInfo, account.measurementSystem)}
            setData={async d => {
              const sd = mapSelected(defaultDevicesWithAxisInfo, d);
              await this.setState({ selectedDevices: sd });
              await this.setState({ headerHeight: getOverviewHeaderHeight() });
              if (this.map) {
                this.map.resize();
                this.map.app.updateSelectedDevices(sd);
              }
              /* if (this.chart) {
                this.chart.updateDimensions();
              } */
            }}
            defaultData={mapToSelect(selectedDevicesWithAxisInfo, account.measurementSystem)}
            isChipsColored
            overviewPage={true}
          />}
        </Header>
        {!isWidthDownSm ? (
          <DesktopView
            identifiersLoading={identifiersLoading}
            isWidthDownMd={isWidthDownMd}
            headerHeight={headerHeight}
            sidebarProps={sidebarProps}
            sidebarSharedProps={sidebarSharedProps}
            sidebarComponentsProps={sidebarComponentsProps}
            sidebarDevicesProps={sidebarDevicesProps}
            handleMeasurementClick={this.handleMeasurementClick}
            {...deviceOverviewProps}
          />
        ) : (
          <MobileView
            shipment={shipment}
            locationsLoading={this.state.locationsLoading}
            selectedDevices={selectedDevices}
            metricsLoadingStatus={metricsLoadingStatus}
            eventsLoadingStatus={eventsLoadingStatus}
            meta={this.props.meta}
            {...deviceOverviewProps}
            {...sidebarSharedProps}
          />)}
      </OverviewCtn>
    );
  }
}

ShipmentOverview.propTypes = propTypes;
